import { CircleCheck, CircleDashed, CircleHelp, Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";

import { type ChecklistStep, progress, type StepStatus } from "./onboarding";
import { useOnboardingPrefs } from "./onboardingStore";
import { useOnboardingStatus } from "./useOnboardingStatus";

const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨"];

const STATUS_ICON: Record<StepStatus, typeof CircleCheck> = {
  done: CircleCheck,
  todo: CircleDashed,
  unknown: CircleHelp,
  no_access: Lock,
};

/**
 * Home-page card "Get your shop ready" (T11-47): nine first-day steps, each green when the
 * service shows it is done; for the owner (`settings.write`) until hidden (Help brings it back).
 */
export function FirstRunChecklist() {
  const { t } = useTranslation("help");
  const { can, session } = useAuth();
  const toasts = useToast();
  const owner = can("settings.write");
  const [prefs, update] = useOnboardingPrefs(session?.user.id);
  const visible = owner && !prefs.checklistHidden;
  const steps = useOnboardingStatus(visible);
  if (!visible) {
    return null;
  }
  const { done, total } = progress(steps);
  const hide = () => {
    update({ checklistHidden: true });
    toasts.success(t("checklist.hidden"));
  };
  return (
    <section
      aria-labelledby="first-run-title"
      data-slot="first-run-checklist"
      data-done={done}
      className="flex flex-col gap-4 rounded-lg border-2 bg-card p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id="first-run-title" className="text-lg font-bold">
            {t("checklist.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {done === total ? t("checklist.all_done") : t("checklist.intro")}
          </p>
        </div>
        <Button type="button" variant="ghost" onClick={hide} data-action="hide-checklist">
          <X className="size-4" aria-hidden />
          {t("checklist.hide")}
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <progress
          className="h-3 w-full max-w-md accent-[#047857]"
          max={total}
          value={done}
          aria-label={t("checklist.progress", { done, total })}
        />
        <span className="shrink-0 text-sm font-semibold" data-slot="checklist-progress">
          {t("checklist.progress", { done, total })}
        </span>
      </div>
      <ol className="grid gap-2 lg:grid-cols-2" data-slot="checklist-steps">
        {steps.map((step) => (
          <StepRow key={step.key} step={step} />
        ))}
      </ol>
    </section>
  );
}

function StepRow({ step }: { step: ChecklistStep }) {
  const { t } = useTranslation("help");
  const Icon = STATUS_ICON[step.status];
  const done = step.status === "done";
  return (
    <li
      data-step={step.key}
      data-status={step.status}
      className={cn(
        "flex items-start gap-3 rounded-md border p-3",
        done ? "border-[#047857] bg-[#ecfdf5]" : "bg-background",
      )}
    >
      <span aria-hidden className="text-lg leading-none font-bold">
        {CIRCLED[step.no - 1]}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-semibold">{t(`checklist.items.${step.key}.title`)}</span>
        <span className="text-sm text-muted-foreground">
          {t(`checklist.items.${step.key}.body`, { percent: step.percent ?? 0 })}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-sm font-medium",
            done ? "text-[#065f46]" : "text-foreground",
          )}
        >
          <Icon className="size-4" aria-hidden />
          {t(`checklist.status.${step.status}`)}
        </span>
      </div>
      {step.status !== "no_access" ? (
        <Button asChild variant={done ? "outline" : "default"} className="shrink-0">
          <Link
            to={step.to}
            aria-label={`${done ? t("checklist.open") : t("checklist.go")} — ${t(`checklist.items.${step.key}.title`)}`}
          >
            {done ? t("checklist.open") : t("checklist.go")}
          </Link>
        </Button>
      ) : null}
    </li>
  );
}
